import React, { useState } from "react";
import { MessageSquarePlus, Bug, Lightbulb, MessageCircle, Send, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";

const TYPES = [
  { value: "bug", label: "Bug", icon: Bug, color: "#FF3B30" },
  { value: "suggestion", label: "Idea", icon: Lightbulb, color: "#E2E30A" },
  { value: "general", label: "General", icon: MessageCircle, color: "#4C9AFF" },
];

export default function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("bug");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!message.trim()) { toast.error("Please tell us what's up"); return; }
    setSending(true);
    try {
      await api.post("/feedback", { type, message: message.trim(), page: window.location.href });
      toast.success("Thanks! Your feedback has been sent.");
      setMessage(""); setType("bug"); setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send feedback. Please try again.");
    } finally { setSending(false); }
  };

  return (
    <>
      <button data-testid="feedback-button" onClick={() => setOpen(true)}
        className="fixed right-0 top-1/2 -translate-y-1/2 z-40 no-print inline-flex items-center gap-2 bg-[#E2E30A] hover:bg-[#c9ca09] text-black px-3 py-4 shadow-lg shadow-black/40 transition-colors uppercase text-xs tracking-wider font-semibold [writing-mode:vertical-rl] rotate-180">
        <MessageSquarePlus className="h-4 w-4 rotate-90" /> Feedback
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="feedback-dialog" className="bg-[#0A0A0A] border-white/10 rounded-none text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-lg">Beta Feedback</DialogTitle>
            <DialogDescription className="text-zinc-200 text-xs">Found a bug or have an idea? Tell us — it goes straight to the team.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div className="grid grid-cols-3 gap-2" data-testid="feedback-type">
              {TYPES.map((t) => {
                const Icon = t.icon;
                const active = type === t.value;
                return (
                  <button key={t.value} data-testid={`feedback-type-${t.value}`} onClick={() => setType(t.value)}
                    className={`flex flex-col items-center gap-1.5 py-3 border text-xs uppercase tracking-wider transition-colors ${active ? "bg-white/5" : "bg-transparent hover:bg-white/5"}`}
                    style={{ borderColor: active ? t.color : "rgba(255,255,255,0.15)", color: active ? t.color : "#e4e4e7" }}>
                    <Icon className="h-4 w-4" /> {t.label}
                  </button>
                );
              })}
            </div>
            <Textarea data-testid="feedback-message" value={message} onChange={(e) => setMessage(e.target.value)}
              placeholder={type === "bug" ? "What happened? What did you expect?" : type === "suggestion" ? "What would make this better?" : "Your message…"}
              rows={5} className="rounded-none bg-[#141414] border-white/10 text-sm resize-none" />
            <Button data-testid="feedback-submit" onClick={submit} disabled={sending}
              className="w-full rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black font-semibold uppercase tracking-wider">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" /> Send Feedback</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
